import type { LayoutBlock } from '@web-studio/shared-schemas';
import { REGISTRY_BLOCK, resolveHeroCopySide, resolveHeroDishSide } from '@web-studio/shared-schemas';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../lib/responsiveImageSizes';
import { pickBlockField } from '../lib/localized';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../lib/resolveSectionHref';
import { LUCIDE_SSR_CLOCK, LUCIDE_SSR_MAP_PIN, resolveSectionSsrIcon } from '../icons/lucideSsrIconNodes';
import { renderLucideIconHtml } from '../icons/renderIconHtml';
import { escapeHtml } from './escape';
import type { BlockHtmlRenderContext } from './renderSiteHtml';

type HeroHtmlRenderer = (block: LayoutBlock, ctx: BlockHtmlRenderContext) => string;

function ctaHrefOptions(block: LayoutBlock, ctx: BlockHtmlRenderContext) {
  return {
    locale: ctx.locale,
    defaultLocale: ctx.defaultLocale,
    content: block.content,
    siteMode: ctx.siteMode,
    pages: ctx.pages,
    allBlocks: ctx.allBlocks,
    publicPathPrefix: ctx.publicPathPrefix,
  };
}

function resolveHeroPrimaryHref(
  block: LayoutBlock,
  ctx: BlockHtmlRenderContext,
  fallback: 'book' | 'browse' = 'book',
): string {
  const intent = readBlockCtaIntent(block.content, ctx.locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback,
  });
  return resolveBlockCtaHref(intent, {
    ...ctaHrefOptions(block, ctx),
    hrefFieldKey: 'ctaPrimaryHref',
  });
}

function resolveHeroSecondaryHref(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const intent = readBlockCtaIntent(block.content, ctx.locale, {
    intentFieldKey: 'ctaSecondaryIntent',
    hrefFieldKey: 'ctaSecondaryHref',
    fallback: 'browse',
  });
  return resolveBlockCtaHref(intent, {
    ...ctaHrefOptions(block, ctx),
    hrefFieldKey: 'ctaSecondaryHref',
  });
}

function renderHeroReelFade(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'book');
  const frameKeys = ['heroImage', 'heroImageSecondary', 'heroImageTertiary'] as const;
  const frames = frameKeys
    .map((key) => {
      const url = block.media[key];
      return typeof url === 'string' && url.trim() ? { key, url: url.trim() } : null;
    })
    .filter((frame): frame is { key: (typeof frameKeys)[number]; url: string } => Boolean(frame));
  const imageAlt = heading || eyebrow || 'Hero';
  const dishSide = resolveHeroDishSide(block.content);
  const copySide = resolveHeroCopySide(dishSide);
  const count = Math.max(frames.length, 1);
  const stageHtml =
    frames.length > 0
      ? frames
          .map((frame, index) => {
            const src = buildSiteImageProxyUrl(frame.url, 1800, 78);
            const srcSet = buildSiteImageSrcSet(frame.url, [480, 768, 1200, 1600, 2000], 78);
            const leadClass = index === 0 ? ' ws-hero-reel__photo--lead' : '';
            const loading = index === 0 ? 'eager' : 'lazy';
            const fetchPriority = index === 0 ? ' fetchpriority="high"' : '';
            const alt = index === 0 ? imageAlt : '';
            return `<img class="ws-hero-reel__photo${leadClass}" src="${escapeHtml(src)}" srcset="${escapeHtml(srcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroFullBleed)}" alt="${escapeHtml(alt)}" width="1600" height="900" loading="${loading}"${fetchPriority} decoding="async" />`;
          })
          .join('')
      : '<div class="ws-hero-reel__placeholder" aria-hidden="true"></div>';

  return `<section class="ws-hero-reel" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" data-dish-side="${escapeHtml(dishSide)}" data-copy-side="${escapeHtml(copySide)}" data-reel-count="${count}" data-reel-paused="false" id="top">
  <div class="ws-hero-reel__stage">${stageHtml}<div class="ws-hero-reel__veil" aria-hidden="true"></div><div class="ws-hero-reel__side-veil" aria-hidden="true"></div></div>
  <div id="about" class="ws-hero-reel__copy"><div class="ws-hero-reel__pocket">
    ${eyebrow ? `<p class="ws-hero-reel__eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
    ${heading ? `<${headingLevel} class="ws-hero-reel__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${subheading ? `<p class="ws-hero-reel__lead">${escapeHtml(subheading)}</p>` : ''}
    ${ctaPrimary ? `<a class="ws-hero-reel__cta" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div></div>
</section>`;
}

function renderHeroFullBleedImage(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'book');
  const heroImage = block.media.heroImage;
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 1800, 78) : '';
  const heroImageSrcSet = heroImage
    ? buildSiteImageSrcSet(heroImage, [480, 768, 1200, 1600, 2000], 78)
    : '';
  const imageAlt = heading || eyebrow || 'Hero';
  const dishSide = resolveHeroDishSide(block.content);
  const copySide = resolveHeroCopySide(dishSide);
  const stageHtml = heroImage
    ? `<img class="ws-hero-fullbleed__photo" src="${escapeHtml(heroImageSrc)}" srcset="${escapeHtml(heroImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroFullBleed)}" alt="${escapeHtml(imageAlt)}" width="1600" height="900" loading="eager" fetchpriority="high" decoding="async" />`
    : '<div class="ws-hero-fullbleed__placeholder" aria-hidden="true"></div>';

  return `<section class="ws-hero-fullbleed" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" data-dish-side="${escapeHtml(dishSide)}" data-copy-side="${escapeHtml(copySide)}" id="top">
  <div class="ws-hero-fullbleed__stage">${stageHtml}<div class="ws-hero-fullbleed__veil" aria-hidden="true"></div><div class="ws-hero-fullbleed__side-veil" aria-hidden="true"></div></div>
  <div id="about" class="ws-hero-fullbleed__copy"><div class="ws-hero-fullbleed__pocket">
    ${eyebrow ? `<p class="ws-hero-fullbleed__eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
    ${heading ? `<${headingLevel} class="ws-hero-fullbleed__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${subheading ? `<p class="ws-hero-fullbleed__lead">${escapeHtml(subheading)}</p>` : ''}
    ${ctaPrimary ? `<a class="ws-hero-fullbleed__cta" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div></div>
</section>`;
}

function renderHeroStandard(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'browse');
  const sealImage = block.media.heroImage;
  const sealSrc = sealImage ? buildSiteImageProxyUrl(sealImage, 240, 78) : '';
  const sealSrcSet = sealImage ? buildSiteImageSrcSet(sealImage, [120, 180, 240], 78) : '';
  const sealHtml = sealSrc
    ? `<div class="ws-hero-monolith__seal"><img class="ws-hero-monolith__seal-img" src="${escapeHtml(sealSrc)}" srcset="${escapeHtml(sealSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroMonolithSeal)}" alt="${escapeHtml(heading || 'Mark')}" width="120" height="120" loading="eager" decoding="async" /></div>`
    : '';

  return `<section class="ws-hero-monolith" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="top">
  <div id="about" class="ws-hero-monolith__inner">
    ${sealHtml}
    <div class="ws-hero-monolith__rule" aria-hidden="true"></div>
    ${heading ? `<${headingLevel} class="ws-hero-monolith__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${subheading ? `<p class="ws-hero-monolith__lead">${escapeHtml(subheading)}</p>` : ''}
    ${ctaPrimary ? `<a class="ws-hero-monolith__link" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div>
</section>`;
}

function renderHeroCoffeeSpotlight(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'browse');
  const badgeText = pickBlockField(block.content, 'badgeText', locale);
  const heroImage = block.media.heroImage;
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 960, 78) : '';
  const heroImageSrcSet = heroImage ? buildSiteImageSrcSet(heroImage, [480, 720, 960], 78) : '';
  const imageAlt = heading || eyebrow || 'Signature';
  const photoHtml = heroImage
    ? `<img class="ws-hero-pedestal__photo" src="${escapeHtml(heroImageSrc)}" srcset="${escapeHtml(heroImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroPedestal)}" alt="${escapeHtml(imageAlt)}" width="720" height="720" loading="eager" fetchpriority="high" decoding="async" />`
    : '<div class="ws-hero-pedestal__placeholder" aria-hidden="true"></div>';

  return `<section class="ws-hero-pedestal" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="top">
  <div class="ws-hero-pedestal__product">
    <div class="ws-hero-pedestal__disk" aria-hidden="true"></div>
    ${photoHtml}
  </div>
  <div id="about" class="ws-hero-pedestal__card">
    ${badgeText ? `<span class="ws-hero-pedestal__badge">${escapeHtml(badgeText)}</span>` : ''}
    ${eyebrow ? `<p class="ws-hero-pedestal__eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
    ${heading ? `<${headingLevel} class="ws-hero-pedestal__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${subheading ? `<p class="ws-hero-pedestal__lead">${escapeHtml(subheading)}</p>` : ''}
    ${ctaPrimary ? `<a class="ws-hero-pedestal__cta" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div>
</section>`;
}

function renderHeroUtilityReservation(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'book');
  const ctaSecondary = pickBlockField(block.content, 'ctaSecondary', locale);
  const ctaSecondaryHref = resolveHeroSecondaryHref(block, ctx);
  const address = pickBlockField(block.content, 'address', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const addressIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'addressIcon', locale),
    LUCIDE_SSR_MAP_PIN,
  );
  const hoursIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'hoursIcon', locale),
    LUCIDE_SSR_CLOCK,
  );
  const heroImage = block.media.heroImage;
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 640, 72) : '';
  const backdropHtml = heroImageSrc
    ? `<img class="ws-hero-desk__backdrop" src="${escapeHtml(heroImageSrc)}" alt="" width="640" height="360" loading="eager" decoding="async" aria-hidden="true" />`
    : '';
  const mapIcon = renderLucideIconHtml(addressIcon.iconNode, {
    size: 'sm',
    className: 'ws-hero-desk__fact-icon',
    name: addressIcon.name,
  });
  const clockIcon = renderLucideIconHtml(hoursIcon.iconNode, {
    size: 'sm',
    className: 'ws-hero-desk__fact-icon',
    name: hoursIcon.name,
  });
  const facts: string[] = [];
  if (address) {
    facts.push(`<li class="ws-hero-desk__fact">${mapIcon}<span class="ws-hero-desk__fact-text">${escapeHtml(address)}</span></li>`);
  }
  if (hours) {
    facts.push(`<li class="ws-hero-desk__fact">${clockIcon}<span class="ws-hero-desk__fact-text">${escapeHtml(hours)}</span></li>`);
  }

  return `<section class="ws-hero-desk" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="top">
  ${backdropHtml}
  <div id="about" class="ws-hero-desk__panel">
    ${heading ? `<${headingLevel} class="ws-hero-desk__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${subheading ? `<p class="ws-hero-desk__lead">${escapeHtml(subheading)}</p>` : ''}
    ${
      ctaPrimary || ctaSecondary
        ? `<div class="ws-hero-desk__actions">${
            ctaPrimary
              ? `<a class="ws-hero-desk__cta" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>`
              : ''
          }${
            ctaSecondary
              ? `<a class="ws-hero-desk__cta-secondary" href="${escapeHtml(ctaSecondaryHref)}">${escapeHtml(ctaSecondary)}</a>`
              : ''
          }</div>`
        : ''
    }
    ${facts.length ? `<ul class="ws-hero-desk__facts">${facts.join('')}</ul>` : ''}
  </div>
</section>`;
}

function renderHeroQuietLuxury(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'book');
  const heroImage = block.media.heroImage;
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 1400, 78) : '';
  const heroImageSrcSet = heroImage ? buildSiteImageSrcSet(heroImage, [480, 768, 1200, 1600], 78) : '';
  const imageAlt = heading || eyebrow || 'Hero';
  const visualHtml = heroImage
    ? `<img class="ws-hero-reveal__photo" src="${escapeHtml(heroImageSrc)}" srcset="${escapeHtml(heroImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroReveal)}" alt="${escapeHtml(imageAlt)}" width="1200" height="900" loading="eager" fetchpriority="high" decoding="async" />`
    : '<div class="ws-hero-reveal__placeholder" aria-hidden="true"></div>';

  return `<section class="ws-hero-reveal" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="top">
  <div class="ws-hero-reveal__visual">${visualHtml}</div>
  <div id="about" class="ws-hero-reveal__copy">
    ${eyebrow ? `<p class="ws-hero-reveal__eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
    ${heading ? `<${headingLevel} class="ws-hero-reveal__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${ctaPrimary ? `<a class="ws-hero-reveal__link" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div>
</section>`;
}

function renderHeroCounterBleed(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'book');
  const heroImage = block.media.heroImage;
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 1800, 78) : '';
  const heroImageSrcSet = heroImage
    ? buildSiteImageSrcSet(heroImage, [480, 768, 1200, 1600, 2000], 78)
    : '';
  const sealImage = block.media.sealImage;
  const sealSrc = sealImage ? buildSiteImageProxyUrl(sealImage, 240, 78) : '';
  const sealSrcSet = sealImage ? buildSiteImageSrcSet(sealImage, [120, 180, 240], 78) : '';
  const imageAlt = heading || eyebrow || 'Hero';
  const stageHtml = heroImage
    ? `<img class="ws-hero-counter__photo" src="${escapeHtml(heroImageSrc)}" srcset="${escapeHtml(heroImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroFullBleed)}" alt="${escapeHtml(imageAlt)}" width="1600" height="900" loading="eager" fetchpriority="high" decoding="async" />`
    : '<div class="ws-hero-counter__placeholder" aria-hidden="true"></div>';
  const sealHtml = sealSrc
    ? `<div class="ws-hero-counter__seal"><img class="ws-hero-counter__seal-img" src="${escapeHtml(sealSrc)}" srcset="${escapeHtml(sealSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroMonolithSeal)}" alt="${escapeHtml(heading || 'Mark')}" width="120" height="120" loading="eager" decoding="async" /></div>`
    : '';

  return `<section class="ws-hero-counter" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="top">
  <div class="ws-hero-counter__stage">${stageHtml}<div class="ws-hero-counter__veil" aria-hidden="true"></div></div>
  <div id="about" class="ws-hero-counter__copy"><div class="ws-hero-counter__pocket">
    ${sealHtml}
    ${eyebrow ? `<p class="ws-hero-counter__eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
    ${heading ? `<${headingLevel} class="ws-hero-counter__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${subheading ? `<p class="ws-hero-counter__lead">${escapeHtml(subheading)}</p>` : ''}
    ${ctaPrimary ? `<a class="ws-hero-counter__link" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div></div>
</section>`;
}

function renderHeroCollageStory(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'browse');
  const mainImage = block.media.heroImage;
  const secondaryImage = block.media.heroImageSecondary;
  const tertiaryImage = block.media.heroImageTertiary;
  const mainSrc = mainImage ? buildSiteImageProxyUrl(mainImage, 960, 78) : '';
  const mainSrcSet = mainImage ? buildSiteImageSrcSet(mainImage, [480, 720, 960], 78) : '';
  const secondarySrc = secondaryImage ? buildSiteImageProxyUrl(secondaryImage, 480, 78) : '';
  const tertiarySrc = tertiaryImage ? buildSiteImageProxyUrl(tertiaryImage, 480, 78) : '';
  const mainHtml = mainSrc
    ? `<img class="ws-hero-zine__img ws-hero-zine__img--main" src="${escapeHtml(mainSrc)}" srcset="${escapeHtml(mainSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroZineMain)}" alt="${escapeHtml(heading || eyebrow || 'Story')}" width="720" height="540" loading="eager" fetchpriority="high" decoding="async" />`
    : '<div class="ws-hero-zine__img ws-hero-zine__img--main ws-hero-zine__placeholder" aria-hidden="true"></div>';

  return `<section class="ws-hero-zine" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="top">
  <div class="ws-hero-zine__collage">
    ${mainHtml}
    ${secondarySrc ? `<img class="ws-hero-zine__img ws-hero-zine__img--secondary" src="${escapeHtml(secondarySrc)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroZineDetail)}" alt="" width="320" height="240" loading="lazy" decoding="async" />` : ''}
    ${tertiarySrc ? `<img class="ws-hero-zine__img ws-hero-zine__img--tertiary" src="${escapeHtml(tertiarySrc)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroZineDetail)}" alt="" width="320" height="240" loading="lazy" decoding="async" />` : ''}
  </div>
  <div id="about" class="ws-hero-zine__copy">
    <div class="ws-hero-zine__block">
      ${eyebrow ? `<p class="ws-hero-zine__eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
      ${heading ? `<${headingLevel} class="ws-hero-zine__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
      ${subheading ? `<p class="ws-hero-zine__lead">${escapeHtml(subheading)}</p>` : ''}
    </div>
    ${ctaPrimary ? `<a class="ws-hero-zine__cta" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div>
</section>`;
}

function renderHeroBrothSteam(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, headingLevel } = ctx;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryHref = resolveHeroPrimaryHref(block, ctx, 'book');
  const heroImage = block.media.heroImage;
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 1800, 78) : '';
  const heroImageSrcSet = heroImage
    ? buildSiteImageSrcSet(heroImage, [480, 768, 1200, 1600, 2000], 78)
    : '';
  const imageAlt = heading || eyebrow || 'Hero';
  const dishSide = resolveHeroDishSide(block.content);
  const copySide = resolveHeroCopySide(dishSide);
  const stageHtml = heroImage
    ? `<img class="ws-hero-broth__photo" src="${escapeHtml(heroImageSrc)}" srcset="${escapeHtml(heroImageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroFullBleed)}" alt="${escapeHtml(imageAlt)}" width="1600" height="900" loading="eager" fetchpriority="high" decoding="async" />`
    : '<div class="ws-hero-broth__placeholder" aria-hidden="true"></div>';

  return `<section class="ws-hero-broth" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" data-dish-side="${escapeHtml(dishSide)}" data-copy-side="${escapeHtml(copySide)}" id="top">
  <div class="ws-hero-broth__stage">${stageHtml}<div class="ws-hero-broth__steam" aria-hidden="true"><span class="ws-hero-broth__plume ws-hero-broth__plume--a"></span><span class="ws-hero-broth__plume ws-hero-broth__plume--b"></span><span class="ws-hero-broth__plume ws-hero-broth__plume--c"></span></div><div class="ws-hero-broth__mist" aria-hidden="true"></div></div>
  <div id="about" class="ws-hero-broth__copy"><div class="ws-hero-broth__pocket">
    ${eyebrow ? `<p class="ws-hero-broth__eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
    ${heading ? `<${headingLevel} class="ws-hero-broth__title">${escapeHtml(heading)}</${headingLevel}>` : ''}
    ${subheading ? `<p class="ws-hero-broth__lead">${escapeHtml(subheading)}</p>` : ''}
    ${ctaPrimary ? `<a class="ws-hero-broth__cta" href="${escapeHtml(ctaPrimaryHref)}">${escapeHtml(ctaPrimary)}</a>` : ''}
  </div></div>
</section>`;
}

const P = REGISTRY_BLOCK.presentation;

/** SSR renderers keyed by registry block key — wired into renderSiteHtml. */
export const HERO_SSR_REGISTRY_OVERRIDES: Partial<Record<string, HeroHtmlRenderer>> = {
  [P.heroFullBleedImage]: renderHeroFullBleedImage,
  [P.heroReelFade]: renderHeroReelFade,
  [P.heroBrothSteam]: renderHeroBrothSteam,
  [P.heroStandard]: renderHeroStandard,
  [P.heroCoffeeSpotlight]: renderHeroCoffeeSpotlight,
  [P.heroUtilityReservation]: renderHeroUtilityReservation,
  [P.heroQuietLuxury]: renderHeroQuietLuxury,
  [P.heroCounterBleed]: renderHeroCounterBleed,
  [P.heroCollageStory]: renderHeroCollageStory,
};
